<script setup lang="ts">
import { ref } from 'vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useMultiplayerMine } from '@/composables/game/multiplayer-mine'
import mineMagnetIcon from '@/assets/images/multiplayer/mine-magnet-icon.webp'

defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  hide: []
}>()

const { useMagnet, closeUseMagnetOverlay, openMinesCollectedModal } = useMultiplayerMine()

const loading = ref(false)

async function onConfirm() {
  loading.value = true
  try {
    const [result] = await Promise.all([
      useMagnet(),
      new Promise(resolve => setTimeout(resolve, 2000)),
    ])
    closeUseMagnetOverlay()
    openMinesCollectedModal(result.acquiredMinesCount)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <Transition name="fade" mode="out-in">
    <div
      v-if="show"
      class="fixed h-screen max-h-dvh w-screen overflow-hidden left-0 bottom-0 bg-white/[.01] backdrop-blur-[12px] flex flex-col items-center justify-center z-[800] px-6"
    >
      <img :src="mineMagnetIcon" class="w-[120px] h-[120px] object-contain -mt-10 relative z-10" alt="Magnet">

      <div v-if="loading" class="scan-line-wrapper">
        <div class="scan-line" />
      </div>

      <h2 class="title uppercase font-pilat text-[32px] leading-[38px] font-black text-white tracking-tight text-center">
        {{ loading ? "SCANNING..." : "USE MAGNET?" }}
      </h2>
      <div v-if="!loading" class="mt-6 px-8">
        <p class="subtitle text-white text-base font-semibold text-center mb-3">
          Collect all mines on your cards. They become yours to place!
        </p>
        <BaseButton
          class="w-full mb-3"
          :disabled="loading"
          @click="onConfirm"
        >
          YES, COLLECT!
        </BaseButton>
        <BaseButton
          class="w-full"
          style-type="secondary"
          @click="$emit('hide')"
        >
          NO
        </BaseButton>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.title {
  text-shadow: 0 0 16px #000;
  -webkit-text-stroke-width: 1px;
  -webkit-text-stroke-color: #000;
}
.subtitle {
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.88);
}

.scan-line-wrapper {
  position: fixed;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.scan-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 16px;
  background: rgba(0, 220, 220, 0.9);
  box-shadow:
    0 0 8px 4px rgba(0, 220, 220, 0.7),
    0 0 24px 12px rgba(0, 220, 220, 0.3);
  animation: scan 1s ease-in-out infinite alternate;
}

@keyframes scan {
  0%   { top: 0; }
  100% { top: 100%; }
}
</style>
